import { Reducer } from 'redux'

export interface AuditEvent {
  id: string
  action: string
  resourceType: string
  actorType: string | null
  actorName: string | null
  actorEmail: string | null
  actorId: string | null
  occurredAt: string
  diff: Record<string, unknown> | null
  metadata: Record<string, unknown> | null
}

export interface AuditEventsResponse {
  auditEvents: AuditEvent[]
  pageNumber: number
  pageSize: number
  sortBy: string
  sortOrder: string
  count: number
}

export interface CrmAuditLogsState {
  entries: AuditEvent[]
  error: string | null
  isLoading: boolean
  pageSize: number
  pageNumber: number
  noOfPage: number | null
  totalCount: number
  loadedPartnerId: string | null
  fetchRequestId: number
}

export enum CrmAuditLogsActionType {
  FETCH_CRM_AUDIT_LOGS_REQUEST = 'FETCH_CRM_AUDIT_LOGS_REQUEST',
  FETCH_CRM_AUDIT_LOGS_SUCCESS = 'FETCH_CRM_AUDIT_LOGS_SUCCESS',
  FETCH_CRM_AUDIT_LOGS_FAILURE = 'FETCH_CRM_AUDIT_LOGS_FAILURE',
  CLEAR_CRM_AUDIT_LOGS = 'CLEAR_CRM_AUDIT_LOGS',
  SET_CRM_AUDIT_LOGS_PAGE_NUMBER = 'SET_CRM_AUDIT_LOGS_PAGE_NUMBER',
  SET_CRM_AUDIT_LOGS_PAGE_SIZE = 'SET_CRM_AUDIT_LOGS_PAGE_SIZE',
}

type CrmAuditLogsAction =
  | {
      type: CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_REQUEST
      payload: { partnerId: string }
    }
  | {
      type: CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_SUCCESS
      payload: {
        data: AuditEventsResponse
        pageSize: number
        pageNumber: number
        partnerId: string
        requestId: number
      }
    }
  | {
      type: CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_FAILURE
      payload: {
        error: string
        requestId: number
      }
    }
  | { type: CrmAuditLogsActionType.CLEAR_CRM_AUDIT_LOGS }
  | {
      type: CrmAuditLogsActionType.SET_CRM_AUDIT_LOGS_PAGE_NUMBER
      payload: number
    }
  | {
      type: CrmAuditLogsActionType.SET_CRM_AUDIT_LOGS_PAGE_SIZE
      payload: number
    }

const initialState: CrmAuditLogsState = {
  entries: [],
  error: null,
  isLoading: false,
  pageSize: 10,
  pageNumber: 1,
  noOfPage: null,
  totalCount: 0,
  loadedPartnerId: null,
  fetchRequestId: 0,
}

export const crmAuditLogsReducer: Reducer<
  CrmAuditLogsState,
  CrmAuditLogsAction
> = (state = initialState, action) => {
  switch (action.type) {
    case CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_REQUEST: {
      const isPartnerSwitch = action.payload.partnerId !== state.loadedPartnerId

      return {
        ...state,
        fetchRequestId: state.fetchRequestId + 1,
        isLoading: true,
        error: null,
        ...(isPartnerSwitch
          ? {
              entries: [],
              totalCount: 0,
              noOfPage: null,
              loadedPartnerId: null,
            }
          : {}),
      }
    }
    case CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_SUCCESS:
      if (action.payload.requestId !== state.fetchRequestId) {
        return state
      }

      return {
        ...state,
        entries: action.payload.data.auditEvents ?? [],
        totalCount: action.payload.data.count,
        noOfPage: Math.ceil(
          action.payload.data.count / action.payload.pageSize
        ),
        pageSize: action.payload.pageSize,
        pageNumber: action.payload.pageNumber,
        loadedPartnerId: action.payload.partnerId,
        error: null,
        isLoading: false,
      }
    case CrmAuditLogsActionType.FETCH_CRM_AUDIT_LOGS_FAILURE:
      if (action.payload.requestId !== state.fetchRequestId) {
        return state
      }

      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      }
    case CrmAuditLogsActionType.SET_CRM_AUDIT_LOGS_PAGE_NUMBER:
      return {
        ...state,
        pageNumber: action.payload,
      }
    case CrmAuditLogsActionType.SET_CRM_AUDIT_LOGS_PAGE_SIZE:
      return {
        ...state,
        pageSize: action.payload,
      }
    case CrmAuditLogsActionType.CLEAR_CRM_AUDIT_LOGS:
      return initialState
    default:
      return state
  }
}
